/**
 * The arithmetic behind the horizontal scroll control, kept apart from the DOM so it can be tested on its own.
 *
 * Every distance is measured from the start edge of the content, in pixels. In right-to-left reading order the browser
 * counts scrollLeft down from zero, so the caller passes its absolute value and the maths stays one-sided.
 */

export interface ScrollMetrics {
    /** How far the content is scrolled from its start edge. */
    scrolled: number;
    scrollWidth: number;
    clientWidth: number;
}

export interface EdgeState {
    overflows: boolean;
    canScrollToStart: boolean;
    canScrollToEnd: boolean;
}

export interface ThumbGeometry {
    width: number;
    /** Distance of the thumb from the start edge of the track. */
    offset: number;
}

/** scrollLeft is fractional on scaled displays, so both ends allow a pixel of slack. */
export const EDGE_TOLERANCE = 1;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const maxScrollOf = ({scrollWidth, clientWidth}: ScrollMetrics): number => Math.max(0, scrollWidth - clientWidth);

export const edgeState = (metrics: ScrollMetrics): EdgeState => {
    const maxScroll = maxScrollOf(metrics);
    const overflows = maxScroll > EDGE_TOLERANCE;
    return {
        overflows,
        canScrollToStart: overflows && metrics.scrolled > EDGE_TOLERANCE,
        canScrollToEnd: overflows && metrics.scrolled < maxScroll - EDGE_TOLERANCE,
    };
};

/**
 * The thumb is as wide as the visible share of the content, but never narrower than minWidth, so it stays easy to grab
 * on a very long board. It travels over whatever track is left beside it.
 */
export const thumbGeometry = (metrics: ScrollMetrics, trackWidth: number, minWidth: number): ThumbGeometry => {
    if (trackWidth <= 0 || metrics.scrollWidth <= 0) {
        return {width: Math.max(0, trackWidth), offset: 0};
    }

    const visibleShare = metrics.clientWidth / metrics.scrollWidth;
    const width = clamp(trackWidth * visibleShare, Math.min(minWidth, trackWidth), trackWidth);
    const maxScroll = maxScrollOf(metrics);
    const progress = maxScroll > 0 ? clamp(metrics.scrolled / maxScroll, 0, 1) : 0;

    return {width, offset: (trackWidth - width) * progress};
};

/** The scroll distance that puts the thumb at offset, the reverse of thumbGeometry, for dragging. */
export const scrolledForThumbOffset = (
    offset: number,
    metrics: ScrollMetrics,
    trackWidth: number,
    thumbWidth: number,
): number => {
    const travel = trackWidth - thumbWidth;
    if (travel <= 0) {
        return 0;
    }
    const maxScroll = maxScrollOf(metrics);
    return clamp(offset / travel * maxScroll, 0, maxScroll);
};

export interface ItemExtent {
    /** Distance of the item's start edge from the start edge of the content. */
    start: number;
    end: number;
}

/**
 * Where one press of an arrow scrolls to. Next brings the first column or phase that is cut off at the end fully
 * into view, and previous does the same for the one cut off at the start. An item counts as cut off when it reaches
 * into the room kept clear at an edge, because that is where the content fades out. Each stop lines up an item that far
 * from the start edge, which is also where a board that snaps to its columns lands when its scroll padding is the same.
 * An item wider than the view shows its start.
 *
 * @param direction 1 for the end, -1 for the start.
 * @param gapStart The room kept clear at the start edge.
 * @param gapEnd The room kept clear at the end edge.
 */
export const itemTarget = (
    items: ItemExtent[],
    metrics: ScrollMetrics,
    direction: 1 | -1,
    gapStart = 0,
    gapEnd = 0,
): number => {
    const maxScroll = maxScrollOf(metrics);
    const sorted = [...items].sort((a, b) => a.start - b.start);
    const stopOf = (item: ItemExtent): number => clamp(item.start - gapStart, 0, maxScroll);
    const isAhead = (stop: number): boolean => stop > metrics.scrolled + EDGE_TOLERANCE;

    // A column only slightly under the start fade would line up with a move too small to notice. So a stop counts only
    // when it is at least the fade's width back, and the press goes back past that column instead.
    if (direction < 0) {
        const cut = sorted
            .filter(item => item.start < metrics.scrolled + gapStart - EDGE_TOLERANCE)
            .filter(item => stopOf(item) < metrics.scrolled - Math.max(gapStart, EDGE_TOLERANCE))
            .pop();
        return cut === undefined ? 0 : stopOf(cut);
    }

    const visibleEnd = metrics.scrolled + metrics.clientWidth - gapEnd;
    const cut = sorted.find(item => item.end > visibleEnd + EDGE_TOLERANCE);
    if (cut === undefined) {
        return maxScroll;
    }

    // The first stop that shows the cut item whole, or the item's own start when it is wider than the view. The last
    // item is shown whole at the very end, where the end fade is gone.
    const needed = Math.min(cut.end + gapEnd - metrics.clientWidth, maxScroll);
    const reveals = sorted.map(stopOf).find(stop => stop >= needed - EDGE_TOLERANCE) ?? maxScroll;
    const target = Math.min(reveals, stopOf(cut));

    // An item already lined up at the start that still runs past the end has nothing more to reveal, so the press moves
    // on to the next stop instead of standing still.
    return isAhead(target) ? target : sorted.map(stopOf).find(isAhead) ?? maxScroll;
};
